<template>
  <Panel toggleable>
    <template #header>
      <FormSelect
        v-model="model.areaId"
        :name="`areasWithPartnerAndCaretaker[${props.index}].areaId`"
        label="area"
        :options="areas"
        :isDirty="() => false"
        :initialValue="data.areaId"
        :disabled="!editMode || !customerId || isExistingArea"
        untranslated
        singleValueFixed
        autofillSingleValue
      />
    </template>
    <Fieldset v-if="model.areaId" :legend="t('labels.contractsPartiesGiver')">
      <div class="flex">
        <FormLazySelect
          :key="`partner-group-${props.index}-${model.areaId}`"
          v-model="model.partnerGroupId"
          :name="`areasWithPartnerAndCaretaker[${props.index}].partnerGroupId`"
          label="partnerGroup"
          :fetchPagedData="partnerGroupFetchFunction"
          :fetchSingleItem="fetchPartnerGroup"
          :isDirty="() => false"
          :initialValue="data.partnerGroupId"
          :disabled="!editMode"
          filter
          showClear
        />
        <FormLazySelect
          :key="`partner-${props.index}-${model.areaId}-${model.partnerGroupId}`"
          v-model="model.partnerId"
          :name="`areasWithPartnerAndCaretaker[${props.index}].partnerId`"
          label="partner"
          :fetchPagedData="partnerFetchFunction"
          :fetchSingleItem="fetchSinglePartner"
          :isDirty="() => false"
          :initialValue="data.partnerId"
          :disabled="!editMode"
          filter
          showClear
        />
      </div>

      <Divider v-if="editMode || model.caretakerId" />

      <div v-if="editMode || model.caretakerId" class="flex">
        <FormLazySelect
          :key="`caretaker-group-${props.index}-${model.areaId}`"
          v-model="model.caretakerPartnerGroupId"
          :name="`areasWithPartnerAndCaretaker[${props.index}].caretakerPartnerGroupId`"
          label="partnerGroup"
          :fetchPagedData="caretakerPartnerGroupFetchFunction"
          :fetchSingleItem="fetchPartnerGroup"
          :isDirty="() => false"
          :initialValue="data.caretakerPartnerGroupId"
          :disabled="!editMode"
          filter
          showClear
        />
        <FormLazySelect
          :key="`caretaker-${props.index}-${model.areaId}-${model.caretakerPartnerGroupId}`"
          v-model="model.caretakerId"
          :name="`areasWithPartnerAndCaretaker[${props.index}].caretakerId`"
          label="caretaker"
          :fetchPagedData="caretakerFetchFunction"
          :fetchSingleItem="fetchSinglePartner"
          :isDirty="() => false"
          :initialValue="data.caretakerId"
          :disabled="!editMode"
          filter
          showClear
        />
      </div>
    </Fieldset>
  </Panel>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import FormLazySelect from '@/components/forms/FormLazySelect.vue';
import type { SelectModel } from '@/components/forms/FormSelect.vue';
import type {
  LazySelectFetchOptions,
  PaginatedResponse,
} from '@/components/forms/hooks/useLazySelect';
import { usePartnerGroups } from '@/views/partnergroups/hooks/usePartnergroups';
import { usePartners } from '@/views/partners/hooks/usePartners';
import type { PartnerSimpleV2Dto, SimpleNameV1Dto } from '@/openapi';

import type { AreaWithPartnerAndCaretaker } from '../contractsTypes';

const props = defineProps<{
  data: AreaWithPartnerAndCaretaker;
  index: number;
  customerId: string | undefined;
  editMode: boolean;
  areas: SelectModel[];
  isExistingArea: boolean;
}>();

const emit = defineEmits(['update:data']);

const { fetchPartner, fetchPartnerSimplePaginated } = usePartners();
const { fetchPartnerGroup, fetchPartnerGroupSimplePaginated } = usePartnerGroups();
const { t } = useI18n();

const model = ref<AreaWithPartnerAndCaretaker>({ ...props.data });

const clearAreaSelection = (): void => {
  model.value.areaId = '';
  model.value.partnerGroupId = '';
  model.value.partnerId = '';
  model.value.caretakerPartnerGroupId = '';
  model.value.caretakerId = '';
};

watch(
  () => props.data,
  (newData) => {
    Object.assign(model.value, newData);
  },
  { deep: true, immediate: true },
);

watch(
  () => model.value.areaId,
  (newAreaId, oldAreaId) => {
    if (newAreaId === oldAreaId) return;

    model.value.partnerGroupId = '';
    model.value.partnerId = '';
    model.value.caretakerPartnerGroupId = '';
    model.value.caretakerId = '';
  },
);

watch(
  () => props.customerId,
  (newCustomerId, oldCustomerId) => {
    if (newCustomerId === oldCustomerId) return;

    clearAreaSelection();
  },
);

watch(
  () => props.areas,
  (newAreas) => {
    if (!model.value.areaId) return;

    const isSelectedAreaStillAvailable = newAreas.some(
      (area) => String(area.id) === String(model.value.areaId),
    );

    if (!isSelectedAreaStillAvailable) {
      clearAreaSelection();
    }
  },
  { deep: true },
);

const partnerGroupFetchFunction = computed(() => {
  const areaId = model.value.areaId;

  return (
    options: LazySelectFetchOptions,
  ): Promise<PaginatedResponse<SimpleNameV1Dto> | undefined> => {
    return fetchPartnerGroupSimplePaginated(options, areaId);
  };
});

const caretakerPartnerGroupFetchFunction = computed(() => {
  const areaId = model.value.areaId;

  return (
    options: LazySelectFetchOptions,
  ): Promise<PaginatedResponse<SimpleNameV1Dto> | undefined> => {
    return fetchPartnerGroupSimplePaginated(options, areaId);
  };
});

// Create fetch function for partner selection that reacts to partnerGroupId and areaId changes
const partnerFetchFunction = computed(() => {
  const partnerGroupId = model.value.partnerGroupId;
  const areaId = model.value.areaId;

  return (
    options: LazySelectFetchOptions,
  ): Promise<PaginatedResponse<PartnerSimpleV2Dto> | undefined> => {
    return fetchPartnerSimplePaginated({
      filter: partnerGroupId ? { partnerGroupId: { equals: partnerGroupId } } : undefined,
      orderBy: undefined,
      page: options.page,
      limit: options.limit,
      searchString: options.searchString ?? undefined,
      areaId: areaId || undefined,
    });
  };
});

const caretakerFetchFunction = computed(() => {
  const caretakerPartnerGroupId = model.value.caretakerPartnerGroupId;
  const areaId = model.value.areaId;

  return (
    options: LazySelectFetchOptions,
  ): Promise<PaginatedResponse<PartnerSimpleV2Dto> | undefined> => {
    return fetchPartnerSimplePaginated({
      filter: caretakerPartnerGroupId
        ? { partnerGroupId: { equals: caretakerPartnerGroupId } }
        : undefined,
      orderBy: undefined,
      page: options.page,
      limit: options.limit,
      searchString: options.searchString ?? undefined,
      areaId: areaId || undefined,
    });
  };
});

const fetchSinglePartner = async (id: string) => {
  return fetchPartner(id);
};

watch(
  () => model.value.partnerId,
  (newId, oldId) => {
    if (
      newId &&
      !props.isExistingArea &&
      (!model.value.caretakerId || model.value.caretakerId === oldId)
    ) {
      model.value.caretakerId = newId;
    }
  },
);

watch(
  () => model.value.partnerGroupId,
  (newGroupId, oldGroupId) => {
    if (newGroupId !== oldGroupId && model.value.partnerId) {
      model.value.partnerId = '';
    }
  },
);

watch(
  () => model.value.caretakerPartnerGroupId,
  (newGroupId, oldGroupId) => {
    if (newGroupId !== oldGroupId && model.value.caretakerId) {
      model.value.caretakerId = '';
    }
  },
);

watch(
  model,
  (newModel) => {
    emit('update:data', newModel);
  },
  { deep: true },
);
</script>
